import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useEvaluationSessionSocket } from '../../hooks/useEvaluationSessionSocket';

import ApprenantSectionCard from './ApprenantSectionCard';
import ElearningExercisePlayer, { ElearningQuizCardShell } from '../elearning/ElearningExercisePlayer';
import { jsonToExerciseQuestions } from '../elearning/ElearningExerciseEditor';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import {
  EVALUATION_SESSION_STATUT_LABELS,
  TENTATIVE_STATUT_LABELS,
  evaluationAllowsFile,
  evaluationRequiresQuestions,
  type EvaluationSessionType,
} from '../../constants/evaluationSession';
import { useEvaluationCountdown } from '../../hooks/useEvaluationCountdown';
import { resolveApprenantEvaluationAction } from '../../utils/apprenantEvaluationPresentation';
import { keepSessionAlive } from '../../services/api';
import {
  fetchEvaluationTentative,
  fetchInscriptionEvaluations,
  startEvaluationTentative,
  submitEvaluationTentative,
  uploadDepotTravailFile,
} from '../../services/academiaApprenantService';

const SESSION_KEEPALIVE_MS = 10 * 60 * 1000;

type Props = {
  inscriptionId: string;
  onToast?: (message: string, variant: 'success' | 'error') => void;
};

type EvaluationRow = Awaited<ReturnType<typeof fetchInscriptionEvaluations>>[number];

export default function ApprenantEvaluationsSection({ inscriptionId, onToast }: Props) {
  const [rows, setRows] = useState<EvaluationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTentativeId, setActiveTentativeId] = useState('');
  const [player, setPlayer] = useState<Awaited<ReturnType<typeof fetchEvaluationTentative>> | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [fileUrl, setFileUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const examShellRef = useRef<HTMLDivElement>(null);

  const portalUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('onego_user') || '{}') as { id?: string; role?: string };
    } catch {
      return {};
    }
  }, []);

  const toast = (message: string, variant: 'success' | 'error') => onToast?.(message, variant);

  const load = useCallback(async () => {
    try {
      setRows(await fetchInscriptionEvaluations(inscriptionId));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [inscriptionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const refreshPlayer = useCallback(async () => {
    if (!activeTentativeId) {
      setPlayer(null);
      return;
    }
    try {
      const data = await fetchEvaluationTentative(activeTentativeId);
      setPlayer(data);
      if (data.reponsesJson && typeof data.reponsesJson === 'object') {
        setAnswers(data.reponsesJson as Record<string, unknown>);
      }
      setFileUrl(data.urlFichier ?? '');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Chargement impossible', 'error');
      setActiveTentativeId('');
    }
  }, [activeTentativeId, toast]);

  useEffect(() => {
    void refreshPlayer();
  }, [refreshPlayer]);

  const { label: countdownLabel, isExpired } = useEvaluationCountdown(
    player?.statut === 'EN_COURS' ? player.remainingMs : null,
    () => void refreshPlayer(),
  );

  useEffect(() => {
    if (!activeTentativeId || player?.statut !== 'EN_COURS') return undefined;
    void keepSessionAlive();
    const timer = window.setInterval(() => void keepSessionAlive(), SESSION_KEEPALIVE_MS);
    return () => window.clearInterval(timer);
  }, [activeTentativeId, player?.statut]);

  useEvaluationSessionSocket({
    userId: portalUser.id ?? '',
    role: portalUser.role ?? 'APPRENANT',
    sessionId: player?.session?.id ?? '',
    tentativeId: activeTentativeId,
    enabled: Boolean(activeTentativeId && player?.statut === 'EN_COURS'),
    onForceClose: () => {
      toast('Évaluation fermée — vous avez quitté l’écran trop souvent.', 'error');
      setActiveTentativeId('');
      setPlayer(null);
      void load();
    },
  });

  const handleStart = async (tentativeId: string) => {
    try {
      await startEvaluationTentative(tentativeId);
      setActiveTentativeId(tentativeId);
      toast('Chronomètre démarré — mode sécurisé actif.', 'success');
      const shell = examShellRef.current;
      if (shell?.requestFullscreen) {
        try {
          await shell.requestFullscreen();
        } catch {
          /* plein écran non supporté */
        }
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Démarrage impossible', 'error');
    }
  };

  const handleSubmit = async () => {
    if (!activeTentativeId || !player) return;
    setSubmitting(true);
    try {
      const type = player.session.typeEvaluation as EvaluationSessionType;
      const payload: { reponsesJson?: Record<string, unknown>; urlFichier?: string } = {};
      if (evaluationRequiresQuestions(type)) payload.reponsesJson = answers;
      if (evaluationAllowsFile(type) && fileUrl) payload.urlFichier = fileUrl;
      const res = await submitEvaluationTentative(activeTentativeId, payload);
      const submitted = res?.data as { noteSur20?: number; session?: { modeCorrection?: string } } | undefined;
      let message = res?.message ?? 'Évaluation soumise';
      if (
        submitted?.session?.modeCorrection === 'AUTOMATIQUE' &&
        submitted.noteSur20 != null
      ) {
        message = `Évaluation soumise — note : ${submitted.noteSur20}/20`;
      } else if (submitted?.session?.modeCorrection === 'MANUELLE') {
        message = 'Évaluation soumise — votre formateur la corrigera prochainement.';
      }
      toast(message, 'success');
      setActiveTentativeId('');
      setPlayer(null);
      await load();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Soumission impossible', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFile = async (file: File) => {
    setUploading(true);
    try {
      setFileUrl(await uploadDepotTravailFile(file));
      toast('Fichier prêt à être envoyé', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Upload impossible', 'error');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <ApprenantSectionCard title="Évaluations" icon="quiz">
        <p className="text-sm text-on-surface-variant">Chargement…</p>
      </ApprenantSectionCard>
    );
  }

  if (rows.length === 0) {
    return null;
  }

  const questions = player?.session.contenuJson
    ? jsonToExerciseQuestions(player.session.contenuJson)
    : [];

  return (
    <ApprenantSectionCard
      id="evaluations"
      title="Évaluations"
      icon="quiz"
      subtitle="Épreuves planifiées par votre formateur — chronomètre actif pendant la session."
    >
      <div className="space-y-3">
        {rows.map((row) => {
          const sessionOpen = row.session.statut === 'OUVERTE';
          const isActive = activeTentativeId === row.tentativeId;
          const action = resolveApprenantEvaluationAction(row);
          const showTimer =
            isActive &&
            player?.statut === 'EN_COURS' &&
            player.remainingMs != null &&
            player.remainingMs > 0;

          return (
            <article
              key={row.tentativeId}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (!isActive) setActiveTentativeId(row.tentativeId);
              }}
              onKeyDown={(e) => {
                if (!isActive && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  setActiveTentativeId(row.tentativeId);
                }
              }}
              className={`cursor-pointer rounded-xl border p-4 transition hover:border-primary/40 ${
                sessionOpen ? 'border-primary/30 bg-primary/5' : 'border-outline-variant/20 bg-white'
              } ${isActive ? 'ring-2 ring-primary/30' : ''}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-bold">{row.session.titre}</h3>
                  <p className="text-xs text-on-surface-variant">
                    {row.session.typeLabel} · {row.session.dureeMinutes} min ·{' '}
                    {EVALUATION_SESSION_STATUT_LABELS[row.session.statut] ?? row.session.statut}
                  </p>
                  {row.session.instructions && !isActive ? (
                    <p className="mt-2 text-sm text-on-surface-variant">{row.session.instructions}</p>
                  ) : null}
                </div>
                {showTimer ? (
                  <div
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 font-mono text-lg font-bold ${
                      isExpired ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-900'
                    }`}
                    aria-live="polite"
                  >
                    <LucideSymbolIcon name="timer" tone="inherit" size={20} />
                    {countdownLabel}
                  </div>
                ) : row.remainingMs != null && sessionOpen && row.statut === 'EN_ATTENTE' ? (
                  <p className="text-xs text-on-surface-variant">
                    Fenêtre jusqu’au{' '}
                    {row.session.dateFermeture
                      ? new Date(row.session.dateFermeture).toLocaleString('fr-FR')
                      : '—'}
                  </p>
                ) : null}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-surface-container-high px-2 py-0.5 text-xs">
                  {TENTATIVE_STATUT_LABELS[row.statut] ?? row.statut}
                  {row.noteSur20 != null ? ` · ${row.noteSur20}/20` : ''}
                </span>
                {'commentaireFormateur' in row && row.commentaireFormateur ? (
                  <p className="w-full text-sm text-on-surface-variant">{String(row.commentaireFormateur)}</p>
                ) : null}
                {action.hint && !isActive ? (
                  <p className="w-full text-xs text-amber-800">{action.hint}</p>
                ) : null}
                {!isActive && action.openable ? (
                  <button
                    type="button"
                    className="rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTentativeId(row.tentativeId);
                    }}
                  >
                    {action.label}
                  </button>
                ) : null}
                {isActive && player?.canStart && player.statut === 'EN_ATTENTE' ? (
                  <button
                    type="button"
                    className="rounded-lg bg-primary px-3 py-1.5 text-sm font-bold text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleStart(row.tentativeId);
                    }}
                  >
                    Commencer l&apos;épreuve
                  </button>
                ) : null}
                {isActive && player?.canSubmit && !isExpired ? (
                  <button
                    type="button"
                    disabled={submitting}
                    className="rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-bold text-white disabled:opacity-60"
                    onClick={() => void handleSubmit()}
                  >
                    {submitting ? 'Envoi…' : 'Soumettre'}
                  </button>
                ) : null}
                {isActive ? (
                  <button
                    type="button"
                    className="rounded-lg border px-3 py-1.5 text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveTentativeId('');
                      setPlayer(null);
                    }}
                  >
                    Fermer
                  </button>
                ) : null}
              </div>

              {isActive &&
              player &&
              !player.canStart &&
              player.statut !== 'EN_COURS' &&
              player.statut !== 'SOUMIS' ? (
                <div
                  className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="font-bold text-amber-950">Épreuve pas encore accessible</p>
                  <p className="mt-2 text-sm text-amber-900">
                    {action.hint ??
                      'Revenez lorsque votre formateur ouvrira la session ou à la date prévue.'}
                  </p>
                  {player.session.instructions ? (
                    <p className="mt-3 text-left text-sm text-on-surface-variant">
                      {player.session.instructions}
                    </p>
                  ) : null}
                </div>
              ) : null}

              {isActive && player?.statut === 'SOUMIS' ? (
                <div
                  className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4"
                  onClick={(e) => e.stopPropagation()}
                >
                  <p className="font-bold text-emerald-900">
                    Épreuve soumise
                    {player.noteSur20 != null ? ` — ${player.noteSur20}/20` : ''}
                  </p>
                  {player.commentaireFormateur ? (
                    <p className="mt-2 text-sm text-on-surface-variant">{player.commentaireFormateur}</p>
                  ) : null}
                </div>
              ) : null}

              {isActive && player?.statut === 'EN_COURS' && !isExpired ? (
                <div
                  ref={isActive ? examShellRef : undefined}
                  className="mt-4 rounded-xl border-2 border-amber-300 bg-white p-4 shadow-lg"
                >
                  <p className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Mode sécurisé : restez sur cette page pendant l’épreuve (changement d’onglet comptabilisé).
                  </p>
                  {evaluationRequiresQuestions(player.session.typeEvaluation as EvaluationSessionType) &&
                  questions.length > 0 ? (
                    <ElearningExercisePlayer
                      questions={questions}
                      value={answers}
                      onChange={setAnswers}
                    />
                  ) : evaluationAllowsFile(player.session.typeEvaluation as EvaluationSessionType) ? (
                    <ElearningQuizCardShell
                      title="Dépôt de travail"
                      meta="Fichier PDF ou document — visible uniquement par votre formateur"
                    >
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,image/*"
                        disabled={uploading}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void handleFile(f);
                        }}
                      />
                      {fileUrl ? (
                        <p className="mt-2 text-sm text-emerald-800">Fichier joint — cliquez sur Soumettre.</p>
                      ) : null}
                    </ElearningQuizCardShell>
                  ) : (
                    <p className="text-sm text-on-surface-variant">
                      Rédigez votre réponse puis soumettez avant la fin du chronomètre.
                    </p>
                  )}
                </div>
              ) : null}

              {isActive &&
              player?.canStart &&
              player.statut === 'EN_ATTENTE' &&
              player.session.dateFermeture ? (
                <p
                  className="mt-3 text-xs text-on-surface-variant"
                  onClick={(e) => e.stopPropagation()}
                >
                  Fenêtre jusqu&apos;au{' '}
                  {new Date(player.session.dateFermeture).toLocaleString('fr-FR')}
                </p>
              ) : null}

              {isActive && player?.statut === 'EN_COURS' && isExpired ? (
                <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                  Le temps est écoulé — la fenêtre d’évaluation est fermée.
                </p>
              ) : null}

              {isActive && player?.statut === 'EXPIRE' ? (
                <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
                  Le délai imparti pour cette épreuve est dépassé.
                </p>
              ) : null}
            </article>
          );
        })}
      </div>
    </ApprenantSectionCard>
  );
}
